/**
 * Screen 02 «Сегодня» as data (salon-mockup.html screen 02, 2026-10-02;
 * concepts/today-final.html). Pure: no React, no fetching.
 *
 * Today and the next 6 days, then «Дальше — в Календаре». Day 0 comes from
 * /today (its gaps start at now, G3); days 1-6 come from /week, which takes
 * Mondays only, so the window is cut out of this week and the next.
 */
import { agendaItems, type AgendaItem } from '../../components/Agenda';
import { addDays, mondayOf } from '../../lib/time';
import type { Appointment, DayView, TodayView } from '../../types';

export const DAYS_SHOWN = 7;

/** The two Mondays /week is asked for: this week's and the next one's. */
export function weekMondays(today: string): [string, string] {
  const monday = mondayOf(today);
  return [monday, addDays(monday, 7)];
}

/** The day «Дальше — в Календаре» opens: the first one not on Today. */
export function firstDayAfter(today: string): string {
  return addDays(today, DAYS_SHOWN);
}

/**
 * Days 1-6 after today, in order, cut out of the weeks /week returned.
 * A day not loaded yet is undefined, so the list can hold its place.
 */
export function followingDays(today: string, weeks: (DayView[] | undefined)[]): (DayView | undefined)[] {
  const byDate = new Map<string, DayView>();
  for (const week of weeks) for (const day of week ?? []) byDate.set(day.date, day);
  return Array.from({ length: DAYS_SHOWN - 1 }, (_, i) => byDate.get(addDays(today, i + 1)));
}

const ended = (endsAt: string, now: string) => Date.parse(endsAt) <= Date.parse(now);

export interface FinishedSummary {
  done: number;
  noShows: number;
  /** Sum of the visits that happened; a no-show earns nothing. */
  earned: number;
}

export function finishedSummary(finished: Appointment[]): FinishedSummary {
  const done = finished.filter((a) => a.status !== 'no_show');
  return {
    done: done.length,
    noShows: finished.length - done.length,
    earned: done.reduce((sum, a) => sum + Number(a.price), 0),
  };
}

/** A mark not saved yet: shown at once, sent when the 5-second undo bar closes. */
export type Mark = 'completed' | 'no_show';
export type Marks = Readonly<Record<string, Mark>>;

/** «Пришёл / Не пришёл» appear this many minutes before a visit starts. */
export const MARK_OPENS_MIN = 15;

const unmarkedStatus = (a: Appointment) => a.status === 'booked' || a.status === 'confirmed';
const holds = (a: Appointment) => a.status !== 'cancelled' && a.status !== 'no_show';
const at = (iso: string) => Date.parse(iso);

/** The one highlighted card: «Скоро · через N мин», red «Опаздывает N мин», or «Сейчас · ещё N мин». */
export type Highlight =
  | { id: string; phase: 'soon' | 'late'; minutes: number }
  | { id: string; phase: 'now'; minutes: number; pct: number };

export interface TodayBoard {
  /** Marked visits that are over, and every no-show, oldest first: rows under «Завершено». */
  finished: Appointment[];
  /** Visits whose buttons have opened, still unmarked, and not the highlighted one: the «Не отмечено» line. */
  unmarked: Appointment[];
  highlight: Highlight | null;
  /** The rest of today as cards, breaks and free slots, in time order. */
  rows: AgendaItem[];
  /** Header «X / Y сом»: X counts only «Пришёл»; Y is every visit except no-shows. */
  earned: number;
  expected: number;
}

/**
 * Screen 02 at `day.now`, with the marks not saved yet laid over the server's
 * statuses (concepts/today-approved.html, model()). Nothing is marked by the
 * clock: a visit that ended unmarked waits in «Не отмечено» until she taps.
 */
export function todayBoard(day: TodayView, marks: Marks = {}): TodayBoard {
  const now = at(day.now);
  const visits = day.appointments
    .filter((a) => a.status !== 'cancelled')
    .map((a) => (marks[a.id] ? { ...a, status: marks[a.id] } : a))
    .sort((p, q) => p.starts_at.localeCompare(q.starts_at));
  const opened = (a: Appointment) => now >= at(a.starts_at) - MARK_OPENS_MIN * 60_000;
  const over = (a: Appointment) => now >= at(a.ends_at);

  const running = visits.filter((a) => opened(a) && !over(a) && a.status !== 'no_show');
  const hi = running.length ? running[running.length - 1] : null;
  const unmarked = visits.filter((a) => unmarkedStatus(a) && opened(a) && a.id !== hi?.id);
  const finished = visits.filter((a) => a.status === 'no_show' || (a.status === 'completed' && over(a)));
  const away = new Set([...unmarked, ...finished].map((a) => a.id));

  let highlight: Highlight | null = null;
  if (hi) {
    const start = at(hi.starts_at);
    if (hi.status === 'completed') {
      const from = Math.max(now, start);
      const total = at(hi.ends_at) - start;
      highlight = {
        id: hi.id,
        phase: 'now',
        minutes: Math.ceil((at(hi.ends_at) - from) / 60_000),
        pct: total > 0 ? Math.round(((from - start) / total) * 100) : 0,
      };
    } else if (start > now) highlight = { id: hi.id, phase: 'soon', minutes: Math.ceil((start - now) / 60_000) };
    else highlight = { id: hi.id, phase: 'late', minutes: Math.floor((now - start) / 60_000) };
  }

  const rows: AgendaItem[] = [];
  for (const item of agendaItems({ ...day, appointments: visits })) {
    if (item.kind === 'visit') {
      if (!away.has(item.appt.id)) rows.push(item);
    } else if (item.kind === 'break') {
      if (!ended(item.ends, day.now)) rows.push(item);
    } else rows.push(item);
  }
  // A no-show marked here and not saved yet: the server has no gap for it yet, so draw
  // the rest of her slot as free, as the server will once the mark is saved (D phone).
  for (const a of day.appointments) {
    if (marks[a.id] !== 'no_show' || a.status === 'no_show' || over(a)) continue;
    const from = new Date(Math.max(now, at(a.starts_at))).toISOString();
    const gap = { starts_at: from, ends_at: a.ends_at, minutes: Math.round((at(a.ends_at) - at(from)) / 60_000) };
    if (gap.minutes > 0) rows.push({ kind: 'gap', at: from, gap });
  }
  rows.sort((p, q) => at(p.at) - at(q.at));

  return {
    finished,
    unmarked,
    highlight,
    rows,
    earned: visits.filter((a) => a.status === 'completed').reduce((s, a) => s + Number(a.price), 0),
    expected: visits.filter(holds).reduce((s, a) => s + Number(a.price), 0),
  };
}

/**
 * «Пересекается с Айгерим, 17:30–18:30»: the other visits on the same day whose
 * time crosses this one. Cancelled visits and no-shows hold no time.
 */
export function overlapsWith(appt: Appointment, sameDay: Appointment[]): Appointment[] {
  if (!holds(appt)) return [];
  return sameDay.filter(
    (o) => o.id !== appt.id && holds(o) && at(o.starts_at) < at(appt.ends_at) && at(appt.starts_at) < at(o.ends_at),
  );
}

export type DayShape =
  | { kind: 'off' }
  | { kind: 'empty'; rows: AgendaItem[] }
  | { kind: 'work'; rows: AgendaItem[]; count: number; sum: number };

/**
 * One of days 1-6. A day off is «Выходной» unless something is booked on it
 * anyway; a working day with nothing booked is «Записей нет» plus its free
 * row(s) — the server already returns one gap for the open hours.
 */
export function dayShape(day: DayView): DayShape {
  const rows = agendaItems(day);
  const count = rows.filter((r) => r.kind === 'visit').length;
  if (count > 0) return { kind: 'work', rows, count, sum: Number(day.expected_revenue) };
  if (!day.working_hours) return { kind: 'off' };
  return { kind: 'empty', rows };
}

/**
 * Visits counted in a day's header line: the ones the day's sum counts too —
 * the server leaves cancelled and no-show out of expected_revenue
 * (NOT_HOLDING, app/services/calendar.py). The mock's «7 визитов» on a day of
 * 8 rows with one no-show agrees.
 */
export function visitCount(day: DayView): number {
  return day.appointments.filter((a) => a.status !== 'cancelled' && a.status !== 'no_show').length;
}

/**
 * Which day the header names. -1 is today. A day counts once its band has
 * reached the top of the list (within `reach` px). At the very bottom it is
 * the last day, because a short last day never reaches the top.
 */
export function headerDay(bandTops: number[], atBottom: boolean, reach = 60): number {
  if (atBottom && bandTops.length) return bandTops.length - 1;
  let shown = -1;
  bandTops.forEach((top, i) => {
    if (top <= reach) shown = i;
  });
  return shown;
}
